// iter314 · Texas HS district rail — renders district tiles for the
// "Texas High School Football" section on the network home. Tapping a
// tile deep-links to /texas-hs/{district-slug} which shows the schools
// within (via the existing SchoolBannersRail component).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Users, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reused stadium glyph — identical to SchoolBannersRail so the two
// rails feel like part of the same design system.
const StadiumGlyph = ({ color = '#ffffff' }) => (
  <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="xMidYMid slice">
    <ellipse cx="100" cy="55" rx="90" ry="35" fill="none" stroke={color} strokeWidth="0.8" />
    <ellipse cx="100" cy="55" rx="65" ry="26" fill="none" stroke={color} strokeWidth="0.6" />
    <ellipse cx="100" cy="55" rx="40" ry="17" fill="none" stroke={color} strokeWidth="0.4" />
  </svg>
);

export default function TxHsDistrictsRail() {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/school-network/districts`);
        if (!cancelled) setDistricts(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setDistricts([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || districts.length === 0) return null;

  return (
    <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory no-scrollbar" data-testid="tx-hs-districts-rail">
      {districts.map((d) => (
        <Link
          key={d.slug}
          to={`/texas-hs/${d.slug}`}
          className="group flex-shrink-0 w-[240px] sm:w-[280px] snap-start rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-white/40 transition relative"
          style={{ background: `linear-gradient(135deg, ${d.color} 0%, ${d.color}88 60%, #0a0a0a 100%)` }}
          data-testid={`district-tile-${d.slug}`}
        >
          <div className="absolute inset-0 pointer-events-none">
            <StadiumGlyph color="#ffffff" />
          </div>
          <div className="relative p-4">
            <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-md ring-2 ring-white/30 flex items-center justify-center mb-3">
              <span className="font-display font-black text-lg text-white">
                {(d.name || 'ISD').replace(/[^A-Z]/g, '').slice(0, 3) || d.name.slice(0, 3).toUpperCase()}
              </span>
            </div>
            <p className="font-display text-lg leading-tight uppercase text-white line-clamp-2">
              {d.name}
            </p>
            <p className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/60 font-bold">
              <MapPin className="w-3 h-3" /> {d.region}
            </p>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-white/90 font-semibold">
                <Users className="w-3.5 h-3.5" />
                {d.school_count} school{d.school_count === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/80 font-bold group-hover:text-white transition">
                Open <ArrowRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
